// src/roles/store/PendingTransfers.js
// GOVERNED FILE
//
// SENDER FLOW:
//   Stage 1 — Before hand over:
//     [Hand Over ✓]  [Refuse to Send ✗ — reason mandatory]
//   Stage 2 — After hand over (sender_handed_over: true):
//     "⏳ Waiting for <receiver> to accept"
//
// RECEIVER FLOW:
//   [✓ Accept]  [✕ Reject]
//
// MANAGER FLOW:
//   All actions + Cancel

import { useEffect, useState, useCallback } from "react";

import {
    getPendingTransfers,
} from "../../services/assets/assetReplayService";

import {
    approveTransfer,
    rejectTransfer,
    cancelTransfer,
    confirmHandOver,
} from "../../services/assets/assetTransactionService";

import {
    WORKFLOW_STATES,
} from "../../services/assets/workflowStates";

import {
    TRANSFER_STATUS_LABELS,
} from "../../constants/enums/transferStatus";

const MANAGER_ROLES = ["sm", "store", "manager", "owner"];

export default function PendingTransfers() {

    const [loading,      setLoading]      = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [transfers,    setTransfers]    = useState([]);
    const [error,        setError]        = useState("");
    const [success,      setSuccess]      = useState("");

    // Refuse modal state (sender)
    const [showRefuseModal,  setShowRefuseModal]  = useState(false);
    const [refuseTransferId, setRefuseTransferId] = useState(null);
    const [refuseReason,     setRefuseReason]     = useState("");
    const [refusing,         setRefusing]         = useState(false);

    // Reject modal state (receiver)
    const [showRejectModal,  setShowRejectModal]  = useState(false);
    const [rejectTransferId, setRejectTransferId] = useState(null);
    const [rejectReason,     setRejectReason]     = useState("");
    const [rejecting,        setRejecting]        = useState(false);

    // Cancel modal state (manager)
    const [showCancelModal,  setShowCancelModal]  = useState(false);
    const [cancelTransferId, setCancelTransferId] = useState(null);
    const [cancelReason,     setCancelReason]     = useState("");
    const [cancelling,       setCancelling]       = useState(false);

    const user       = JSON.parse(localStorage.getItem("user") || "{}");
    const company_id = localStorage.getItem("company_id") || user?.company_id;
    const uid        = user?.uid || user?.id || "";
    const role       = (user?.role || "").toLowerCase().trim();
    const isManager  = MANAGER_ROLES.includes(role);

    const loadTransfers = useCallback(async () => {
        try {
            setLoading(true); setError("");
            if (!company_id) throw new Error("company_id missing");
            const response = await getPendingTransfers(company_id);
            const pending  = (response || []).filter(
                t => t.workflow_state === WORKFLOW_STATES.PENDING_ACCEPTANCE
            );
            const filtered = isManager
                ? pending
                : pending.filter(t => t.to_id === uid || t.from_id === uid);
            setTransfers(filtered);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load pending transfers");
        } finally { setLoading(false); }
    }, [company_id, uid, isManager]);

    useEffect(() => { loadTransfers(); }, [loadTransfers]);

    // ── HAND OVER (sender confirms physical delivery) ──
    const handleHandOver = async (transaction_id) => {
        if (!window.confirm("Confirm you have physically handed over this asset to the receiver?")) return;
        try {
            setProcessingId(transaction_id); setError(""); setSuccess("");
            await confirmHandOver({ transaction_id, handed_over_by: uid, company_id });
            setSuccess("Hand over confirmed ✅ — waiting for receiver to accept.");
            await loadTransfers();
        } catch (err) { setError(err.message || "Failed to confirm hand over"); }
        finally { setProcessingId(null); }
    };

    // ── REFUSE TO SEND (sender refuses — opens modal) ──
    const openRefuseModal = (transaction_id) => {
        setRefuseTransferId(transaction_id);
        setRefuseReason("");
        setShowRefuseModal(true);
    };

    const handleRefuse = async () => {
        if (!refuseReason.trim()) { alert("Reason is mandatory."); return; }
        try {
            setRefusing(true); setError(""); setSuccess("");
            await cancelTransfer({
                transaction_id:     refuseTransferId,
                cancelled_by:       uid,
                cancellation_reason: refuseReason.trim(),
                cancelled_by_role:  "sender",
                company_id,
            });
            setShowRefuseModal(false);
            setRefuseReason("");
            setRefuseTransferId(null);
            setSuccess("Transfer refused — asset remains with you.");
            await loadTransfers();
        } catch (err) { setError(err.message || "Failed to refuse transfer"); }
        finally { setRefusing(false); }
    };

    // ── ACCEPT (receiver accepts custody) ──
    const handleAccept = async (transaction_id) => {
        if (!window.confirm("Accept this transfer? Custody will transfer to you.")) return;
        try {
            setProcessingId(transaction_id); setError(""); setSuccess("");
            await approveTransfer({ transaction_id, approved_by: uid, company_id });
            setSuccess("Transfer accepted ✅ — asset is now in your custody.");
            await loadTransfers();
        } catch (err) { setError(err.message || "Failed to accept"); }
        finally { setProcessingId(null); }
    };

    // ── REJECT (receiver — opens modal for reason) ──
    const openRejectModal = (transaction_id) => {
        setRejectTransferId(transaction_id);
        setRejectReason("");
        setShowRejectModal(true);
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) { alert("Rejection reason is mandatory."); return; }
        try {
            setRejecting(true); setError(""); setSuccess("");
            await rejectTransfer({
                transaction_id:   rejectTransferId,
                rejected_by:      uid,
                rejection_reason: rejectReason.trim(),
                company_id,
            });
            setShowRejectModal(false);
            setRejectReason("");
            setRejectTransferId(null);
            setSuccess("Transfer rejected — asset remains with sender.");
            await loadTransfers();
        } catch (err) { setError(err.message || "Failed to reject"); }
        finally { setRejecting(false); }
    };

    // ── CANCEL (manager — opens modal for reason) ──
    const openCancelModal = (transaction_id) => {
        setCancelTransferId(transaction_id);
        setCancelReason("");
        setShowCancelModal(true);
    };

    const handleCancel = async () => {
        if (!cancelReason.trim()) { alert("Cancellation reason is mandatory."); return; }
        try {
            setCancelling(true); setError(""); setSuccess("");
            await cancelTransfer({
                transaction_id:      cancelTransferId,
                cancelled_by:        uid,
                cancellation_reason: cancelReason.trim(),
                cancelled_by_role:   "manager",
                company_id,
            });
            setShowCancelModal(false);
            setCancelReason("");
            setCancelTransferId(null);
            setSuccess("Transfer cancelled.");
            await loadTransfers();
        } catch (err) { setError(err.message || "Failed to cancel"); }
        finally { setCancelling(false); }
    };

    const formatDate = (ts) => {
        if (!ts) return "-";
        try { return ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "-"; }
        catch { return "-"; }
    };

    const getStatusColor = (s) => ({
        [WORKFLOW_STATES.PENDING_ACCEPTANCE]: "#ea580c",
        [WORKFLOW_STATES.RETURN_PENDING]:     "#7c3aed",
        [WORKFLOW_STATES.FINALIZED]:          "#16a34a",
        [WORKFLOW_STATES.REJECTED]:           "#dc2626",
        [WORKFLOW_STATES.CANCELLED]:          "#6b7280",
    }[s] || "#6b7280");

    const getTransferRole = (t) => {
        if (t.to_id   === uid) return "RECEIVER";
        if (t.from_id === uid) return "SENDER";
        return "MANAGER_VIEW";
    };

    return (
        <div style={{ padding: "20px" }}>

            <div style={{ marginBottom: "20px" }}>
                <h2>Pending Transfers</h2>
                <p>Asset transfer requests waiting for action</p>
            </div>

            {error   && <div style={{ background: "#ffe5e5", color: "#d00000", padding: "12px", borderRadius: "8px", marginBottom: "16px" }}>{error}</div>}
            {success && <div style={{ background: "#e7ffe7", color: "#008000", padding: "12px", borderRadius: "8px", marginBottom: "16px" }}>{success}</div>}

            {loading ? (
                <div>Loading...</div>

            ) : transfers.length === 0 ? (
                <div style={{ background: "#fff", padding: "32px 20px", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)", textAlign: "center" }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>✅</div>
                    <div style={{ color: "#6b7280" }}>No pending transfers</div>
                </div>

            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {transfers.map(transfer => {
                        const transferRole   = getTransferRole(transfer);
                        const isReceiver     = transferRole === "RECEIVER";
                        const isSender       = transferRole === "SENDER";
                        const isProcessing   = processingId === transfer.id;
                        const handedOver     = transfer.sender_handed_over === true;

                        return (
                            <div key={transfer.id} style={{
                                background: "#fff", borderRadius: "12px",
                                padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                                borderLeft: isReceiver ? "4px solid #2563eb"
                                    : isSender         ? "4px solid #f59e0b"
                                    : "4px solid #e5e7eb",
                            }}>

                                {/* TOP ROW */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
                                    <div>
                                        <h3 style={{ margin: 0 }}>{transfer.asset_name}</h3>
                                        <div style={{ color: "#6b7280", marginTop: "4px", fontSize: "13px" }}>
                                            Asset ID: {transfer.asset_id}
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                                        {/* Role context badge */}
                                        {isReceiver && (
                                            <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>
                                                📥 You are RECEIVER
                                            </span>
                                        )}
                                        {isSender && !handedOver && (
                                            <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>
                                                📤 You are SENDER
                                            </span>
                                        )}
                                        {isSender && handedOver && (
                                            <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>
                                                ✅ Handed Over
                                            </span>
                                        )}
                                        {transferRole === "MANAGER_VIEW" && (
                                            <>
                                                <span style={{ background: "#f3f4f6", color: "#6b7280", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>
                                                    👁 Manager View
                                                </span>
                                                {handedOver ? (
                                                    <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>
                                                        ✅ Handed Over
                                                    </span>
                                                ) : (
                                                    <span style={{ background: "#fef9c3", color: "#854d0e", padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700" }}>
                                                        ⚠️ Not Handed Over Yet
                                                    </span>
                                                )}
                                            </>
                                        )}
                                        <span style={{
                                            background: `${getStatusColor(transfer.workflow_state)}20`,
                                            color: getStatusColor(transfer.workflow_state),
                                            padding: "6px 12px", borderRadius: "999px",
                                            fontSize: "12px", fontWeight: "600",
                                        }}>
                                            {TRANSFER_STATUS_LABELS[transfer.workflow_state] || transfer.workflow_state || "-"}
                                        </span>
                                    </div>
                                </div>

                                {/* DETAILS */}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px", marginBottom: "20px" }}>
                                    <InfoItem label="From"         value={transfer.from_snapshot?.holder_name || transfer.from_holder_name || `${transfer.from_type} / ${transfer.from_id}`} />
                                    <InfoItem label="To"           value={transfer.to_snapshot?.holder_name   || transfer.to_holder_name   || `${transfer.to_type} / ${transfer.to_id}`} />
                                    <InfoItem label="Requested By" value={transfer.requested_by_name || transfer.requested_by || "-"} />
                                    <InfoItem label="Serial No"    value={transfer.serial_no || "-"} />
                                    <InfoItem label="Created At"   value={formatDate(transfer.created_at)} />
                                    <InfoItem label="Remarks"      value={transfer.remarks || "-"} />
                                </div>

                                {/* ── ACTIONS ── */}
                                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>

                                    {/* SENDER — Stage 1: not handed over yet */}
                                    {isSender && !handedOver && !isManager && (
                                        <>
                                            <button
                                                onClick={() => handleHandOver(transfer.id)}
                                                disabled={isProcessing}
                                                style={{ ...btnStyle, background: "#16a34a", opacity: isProcessing ? 0.6 : 1 }}>
                                                {isProcessing ? "Processing..." : "✓ Hand Over"}
                                            </button>
                                            <button
                                                onClick={() => openRefuseModal(transfer.id)}
                                                disabled={isProcessing}
                                                style={{ ...btnStyle, background: "#dc2626", opacity: isProcessing ? 0.6 : 1 }}>
                                                ✕ Refuse to Send
                                            </button>
                                        </>
                                    )}

                                    {/* SENDER — Stage 2: handed over, waiting for receiver */}
                                    {isSender && handedOver && !isManager && (
                                        <span style={{ color: "#92400e", fontSize: "13px", fontStyle: "italic" }}>
                                            ⏳ Waiting for {transfer.to_holder_name || "receiver"} to accept
                                        </span>
                                    )}

                                    {/* RECEIVER ONLY: Accept + Reject (with reason) */}
                                    {isReceiver && (
                                        <>
                                            <button
                                                onClick={() => handleAccept(transfer.id)}
                                                disabled={isProcessing}
                                                style={{ ...btnStyle, background: "#2563eb", opacity: isProcessing ? 0.6 : 1 }}>
                                                {isProcessing ? "Processing..." : "✓ Accept"}
                                            </button>
                                            <button
                                                onClick={() => openRejectModal(transfer.id)}
                                                disabled={isProcessing}
                                                style={{ ...btnStyle, background: "#dc2626", opacity: isProcessing ? 0.6 : 1 }}>
                                                ✕ Reject
                                            </button>
                                        </>
                                    )}

                                    {/* MANAGER ONLY: Cancel with reason */}
                                    {isManager && (
                                        <button
                                            onClick={() => openCancelModal(transfer.id)}
                                            disabled={isProcessing}
                                            style={{ ...btnStyle, background: "#6b7280", opacity: isProcessing ? 0.6 : 1 }}>
                                            Cancel Transfer
                                        </button>
                                    )}

                                    {/* MANAGER VIEW — status info */}
                                    {transferRole === "MANAGER_VIEW" && (
                                        <span style={{ color: "#92400e", fontSize: "13px", fontStyle: "italic" }}>
                                            ⏳ Waiting for {transfer.to_holder_name || "receiver"} to accept
                                        </span>
                                    )}

                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── REJECT MODAL — receiver, reason mandatory ── */}
            {showRejectModal && (
                <div style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.6)",
                    display: "flex", alignItems: "flex-end", justifyContent: "center",
                    zIndex: 9999, padding: "16px",
                }}>
                    <div style={{
                        background: "#fff", borderRadius: "16px",
                        width: "100%", maxWidth: "440px",
                        padding: "20px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    }}>
                        <h3 style={{ margin: "0 0 8px", fontSize: "16px", color: "#0f172a" }}>
                            ✕ Reject Transfer
                        </h3>
                        <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                            Asset will remain with the sender. Reason is mandatory for audit.
                        </p>
                        <div style={{ marginBottom: "16px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                                Reason <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <textarea
                                rows={3}
                                placeholder="Why are you rejecting this transfer?"
                                value={rejectReason}
                                onChange={e => setRejectReason(e.target.value)}
                                autoFocus
                                style={{
                                    width: "100%", padding: "10px",
                                    border: `1px solid ${rejectReason.trim() ? "#d1d5db" : "#fca5a5"}`,
                                    borderRadius: "8px", fontSize: "14px",
                                    resize: "none", outline: "none", boxSizing: "border-box",
                                }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "12px" }}>
                            <button
                                onClick={() => { setShowRejectModal(false); setRejectReason(""); }}
                                style={{ flex: 1, padding: "12px", borderRadius: "10px", background: "#f1f5f9", border: "none", fontWeight: "600", cursor: "pointer", color: "#374151" }}>
                                ← Go Back
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={rejecting || !rejectReason.trim()}
                                style={{ flex: 1, padding: "12px", borderRadius: "10px", background: "#dc2626", border: "none", fontWeight: "600", color: "#fff", cursor: rejecting || !rejectReason.trim() ? "not-allowed" : "pointer", opacity: rejecting || !rejectReason.trim() ? 0.5 : 1 }}>
                                {rejecting ? "Processing..." : "Confirm Reject"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CANCEL MODAL — manager only, reason mandatory ── */}
            {showCancelModal && (
                <div style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.6)",
                    display: "flex", alignItems: "flex-end", justifyContent: "center",
                    zIndex: 9999, padding: "16px",
                }}>
                    <div style={{
                        background: "#fff", borderRadius: "16px",
                        width: "100%", maxWidth: "440px",
                        padding: "20px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    }}>
                        <h3 style={{ margin: "0 0 8px", fontSize: "16px", color: "#0f172a" }}>
                            Cancel Transfer
                        </h3>
                        <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                            Transfer will be cancelled. Asset remains with current holder. Reason is mandatory.
                        </p>
                        <div style={{ marginBottom: "16px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                                Reason <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <textarea
                                rows={3}
                                placeholder="Why are you cancelling this transfer?"
                                value={cancelReason}
                                onChange={e => setCancelReason(e.target.value)}
                                autoFocus
                                style={{
                                    width: "100%", padding: "10px",
                                    border: `1px solid ${cancelReason.trim() ? "#d1d5db" : "#fca5a5"}`,
                                    borderRadius: "8px", fontSize: "14px",
                                    resize: "none", outline: "none", boxSizing: "border-box",
                                }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "12px" }}>
                            <button
                                onClick={() => { setShowCancelModal(false); setCancelReason(""); }}
                                style={{ flex: 1, padding: "12px", borderRadius: "10px", background: "#f1f5f9", border: "none", fontWeight: "600", cursor: "pointer", color: "#374151" }}>
                                ← Go Back
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={cancelling || !cancelReason.trim()}
                                style={{ flex: 1, padding: "12px", borderRadius: "10px", background: "#6b7280", border: "none", fontWeight: "600", color: "#fff", cursor: cancelling || !cancelReason.trim() ? "not-allowed" : "pointer", opacity: cancelling || !cancelReason.trim() ? 0.5 : 1 }}>
                                {cancelling ? "Processing..." : "Confirm Cancel"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── REFUSE MODAL — reason mandatory ── */}
            {showRefuseModal && (
                <div style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.6)",
                    display: "flex", alignItems: "flex-end", justifyContent: "center",
                    zIndex: 9999, padding: "16px",
                }}>
                    <div style={{
                        background: "#fff", borderRadius: "16px",
                        width: "100%", maxWidth: "440px",
                        padding: "20px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    }}>
                        <h3 style={{ margin: "0 0 8px", fontSize: "16px", color: "#0f172a" }}>
                            ✕ Refuse to Send
                        </h3>
                        <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
                            Asset will remain with you. Transfer will be cancelled.
                        </p>

                        <div style={{ marginBottom: "16px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", display: "block", marginBottom: "6px" }}>
                                Reason <span style={{ color: "#ef4444" }}>*</span> (mandatory)
                            </label>
                            <textarea
                                rows={3}
                                placeholder="Why are you refusing this transfer?"
                                value={refuseReason}
                                onChange={e => setRefuseReason(e.target.value)}
                                autoFocus
                                style={{
                                    width: "100%", padding: "10px",
                                    border: "1px solid #d1d5db", borderRadius: "8px",
                                    fontSize: "14px", resize: "none",
                                    outline: "none", boxSizing: "border-box",
                                    borderColor: refuseReason.trim() ? "#d1d5db" : "#fca5a5",
                                }}
                            />
                            {!refuseReason.trim() && (
                                <p style={{ color: "#ef4444", fontSize: "11px", marginTop: "4px" }}>
                                    Reason is required
                                </p>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: "12px" }}>
                            <button
                                onClick={() => { setShowRefuseModal(false); setRefuseReason(""); }}
                                style={{ flex: 1, padding: "12px", borderRadius: "10px", background: "#f1f5f9", border: "none", fontWeight: "600", cursor: "pointer", color: "#374151" }}>
                                ← Go Back
                            </button>
                            <button
                                onClick={handleRefuse}
                                disabled={refusing || !refuseReason.trim()}
                                style={{ flex: 1, padding: "12px", borderRadius: "10px", background: "#dc2626", border: "none", fontWeight: "600", cursor: refusing || !refuseReason.trim() ? "not-allowed" : "pointer", color: "#fff", opacity: refusing || !refuseReason.trim() ? 0.5 : 1 }}>
                                {refusing ? "Processing..." : "Confirm Refuse"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function InfoItem({ label, value }) {
    return (
        <div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>{label}</div>
            <div style={{ fontWeight: "600", wordBreak: "break-word" }}>{value || "-"}</div>
        </div>
    );
}

const btnStyle = {
    color: "#ffffff", border: "none",
    padding: "10px 16px", borderRadius: "8px",
    cursor: "pointer", fontWeight: "600",
};